import Box from '@mui/material/Box';
import { config } from '@services/context';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { usePageTitle } from '@shared/hooks/usePageTitle';
import { usePageTracking } from '@shared/hooks/usePageTracking';
import { useScreenSize } from '@shared/hooks/useScreenSize';
import { NetworkCollector } from '@shared/telemetry/NetworkCollector';
import { ReactNode, useEffect, useRef } from 'react';
import { FluxAppBar } from './appbar/FluxAppBar';
import { MigrationBanner, MIGRATION_BANNER_HEIGHT } from './components/MigrationBanner';
import MobileNavBar from './MobileNavBar';

type Props = {
  children: ReactNode;
};

export default function AppFrame({ children }: Props) {
  usePageTitle();
  usePageTracking();
  const { isMobile, isMobileLandscape } = useScreenSize();

  const networkCollectorRef = useRef<NetworkCollector>();
  const user = useUserContext();
  const isCollectingTelemetry = config.isCollectingTelemetry || config.isDev;

  useEffect(() => {
    if (isCollectingTelemetry) {
      if (user?.email) {
        networkCollectorRef.current = new NetworkCollector({
          email: user.email,
          full_name: user.full_name,
          id: user.id,
        });
        networkCollectorRef.current.start();
      } else {
        console.log('Not creating NetworkCollector - user not ready');
        networkCollectorRef.current?.stop();
      }

      const interval = setInterval(() => {
        if (networkCollectorRef.current) {
          const metrics = networkCollectorRef.current.collect();
          // TODO: actually send this somewhere useful rather than spamming the console
          // console.log('Network Metrics:', metrics);

          const slowApis = metrics.filter(m => m.name === 'api_avg_response_time' && m.value > 1000);

          if (slowApis.length > 0) {
            console.log('Slow APIs detected:', slowApis);
          }
        }
      }, 5 * 60 * 1000);

      return () => {
        clearInterval(interval);
        networkCollectorRef.current?.stop();
      };
    } else {
      return;
    }
  }, [user, isCollectingTelemetry]);

  if (isMobile || isMobileLandscape) {
    return (
      <>
        <MigrationBanner />
        <Box paddingTop={`${MIGRATION_BANNER_HEIGHT}px`} paddingBottom={12}>
          {children}
        </Box>
        <MobileNavBar />
      </>
    );
  }

  return <FluxAppBar>{children}</FluxAppBar>;
}
